import { useEffect, useId, useRef } from 'react';

import { useT } from '@/i18n';

import { Button } from './Button';
import styles from './ui.module.css';
import type { ConfirmDialogProps } from './ConfirmDialog.types';

export function ConfirmDialog({ open, title, text, confirmLabel, danger = false, busy = false, onConfirm, onCancel }: ConfirmDialogProps): React.JSX.Element | null {
  const t = useT();
  const id = useId();
  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return undefined;
    cancelRef.current?.focus();
    const onKey = (event: globalThis.KeyboardEvent): void => {
      if (event.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [open, onCancel]);

  if (!open) return null;
  return (
    <div
      className={styles.backdrop}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onCancel();
      }}
    >
      <div role="dialog" aria-modal="true" aria-labelledby={`${id}-title`} aria-describedby={text ? `${id}-text` : undefined} className={styles.modal}>
        <h2 id={`${id}-title`} className={styles.modalTitle}>
          {title}
        </h2>
        {text ? (
          <div className={styles.modalBody} tabIndex={0}>
            <p id={`${id}-text`} className={styles.modalText}>
              {text}
            </p>
          </div>
        ) : null}
        <div className={styles.modalActions}>
          {/* as everywhere in Bridge, buttons sit on the left, the main one first; focus still starts on Cancel */}
          <Button variant={danger ? 'danger' : 'primary'} size="small" onClick={onConfirm} disabled={busy}>
            {confirmLabel}
          </Button>
          <Button ref={cancelRef} variant="secondary" size="small" onClick={onCancel} disabled={busy}>
            {t('common.cancel')}
          </Button>
        </div>
      </div>
    </div>
  );
}
